import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';

/**
 * Locales, die von rechts nach links gelesen werden.
 *
 * Modulebene statt `static #rtlLocales`: bei aktivem `experimentalDecorators`
 * (tsconfig.json) verbietet TypeScript statische private Identifier in einer
 * dekorierten Klasse (TS18036).
 */
const RTL_LOCALES = new Set(['ar', 'fa', 'he', 'ur']);

/**
 * Hält das `lang`- (und `dir`-) Attribut des <html>-Elements auf der aktiven
 * UI-Sprache. Screenreader entscheiden anhand von `lang`, mit welcher Aussprache
 * sie eine Seite vorlesen; ausserdem hängen Silbentrennung und
 * Anführungszeichen-Formen des Browsers daran. WCAG 2.2 SC 3.1.1.
 *
 * Dies ist die einzige Stelle der App, die <html lang> zur Laufzeit schreibt.
 * `init()` wird einmalig von der AppComponent aufgerufen -- zu diesem Zeitpunkt
 * ist der APP_INITIALIZER durch und das Locale steht fest.
 */
@Injectable({ providedIn: 'root' })
export class HtmlLangService {
  readonly #document = inject(DOCUMENT);
  readonly #translate = inject(TranslateService);
  readonly #destroyRef = inject(DestroyRef);

  #initialized = false;

  init(): void {
    if (this.#initialized) {
      return;
    }
    this.#initialized = true;

    this.#apply(this.#translate.currentLang);

    this.#translate.onLangChange
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((event) => this.#apply(event.lang));
  }

  /**
   * Setzt `lang`/`dir`. Ohne bekanntes Locale bleibt der Wert aus index.html
   * stehen -- lieber die Vorbelegung behalten als das Attribut leeren.
   */
  #apply(locale: string | null | undefined): void {
    if (!locale) {
      return;
    }

    const root = this.#document.documentElement;
    root.lang = locale;
    root.dir = RTL_LOCALES.has(locale.slice(0, 2).toLowerCase()) ? 'rtl' : 'ltr';
  }
}
